import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import { getFloat } from "@/utils/mappings";

import ExpandableSection from "../../../../components/job/ExpandableSection";

const cardName = "additionalForcesAndMomentsSection";
const additionalForcesAndMomentsSectionSchema = z.object({
  applied_sheared_load: z.union([
    z.number().gt(0),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
  applied_axial_load: z.union([
    z.number().gt(0),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
  applied_tortional_moment: z.union([
    z.number().gt(0),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
  applied_axial_moment: z.union([
    z.number().gt(0),
    z.string().refine((value) => !isNaN(Number.parseFloat(value)), { message: "Must be a number" }),
  ]),
});
type AdditionalForcesAndMomentsSectionSchema = z.infer<typeof additionalForcesAndMomentsSectionSchema>;
export function AdditionalForcesAndMoments() {
  const { sections, toggleExpandableSection, setAdditionalForcesAndMomentsSections } = useCUCompositeJobStore();
  const savedData = sections.additionalForcesAndMomentsSection?.data;

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<AdditionalForcesAndMomentsSectionSchema>({
    resolver: zodResolver(additionalForcesAndMomentsSectionSchema),
    values: {
      applied_sheared_load: savedData?.applied_sheared_load ?? "",
      applied_axial_load: savedData?.applied_axial_load ?? "",
      applied_tortional_moment: savedData?.applied_tortional_moment ?? "",
      applied_axial_moment: savedData?.applied_axial_moment ?? "",
    },
  });

  return (
    <ExpandableSection
      status={sections.additionalForcesAndMomentsSection?.status ?? "hidden"}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Additional forces and moments"
    >
      <div>
        <form
          className="flex flex-col flex-wrap items-center gap-6 md:flex-row md:gap-20"
          onSubmit={handleSubmit((data) => {
            setAdditionalForcesAndMomentsSections({ data, status: "done" });
          })}
        >
          <TextInput
            className="flex-1 basis-full md:basis-60"
            label="Applied sheared load"
            type="number"
            step={inputStep}
            error={errors.applied_sheared_load?.message}
            {...register("applied_sheared_load", { setValueAs: getFloat })}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                lbf
              </Typography>
            }
          />
          <TextInput
            className="flex-1 basis-full md:basis-60"
            label="Applied axial load"
            type="number"
            step={inputStep}
            error={errors.applied_axial_load?.message}
            {...register("applied_axial_load", { setValueAs: getFloat })}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                lbf
              </Typography>
            }
          />
          <TextInput
            className="flex-1 basis-full md:basis-60"
            label="Applied tortional moment"
            type="number"
            step={inputStep}
            error={errors.applied_tortional_moment?.message}
            {...register("applied_tortional_moment", { setValueAs: getFloat })}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                ft*lbf
              </Typography>
            }
          />
          <TextInput
            className="flex-1 basis-full md:basis-60"
            label="Applied axial moment"
            type="number"
            step={inputStep}
            error={errors.applied_axial_moment?.message}
            {...register("applied_axial_moment", { setValueAs: getFloat })}
            endAdornment={
              <Typography variant="text-small" weight="regular" className="text-gray-400">
                ft*lbf
              </Typography>
            }
          />
          <Button className="w-full self-end md:w-fit" type="submit">
            Save
          </Button>
        </form>
      </div>
    </ExpandableSection>
  );
}
